import type { DashboardType } from '@/types/dashboard';
import { useDashboard } from '@/hooks/use-dashboard';
import { BarChart3, Package } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DashboardTabsProps {
  readonly value: DashboardType;
  readonly onChange: (value: DashboardType) => void;
  readonly className?: string;
  readonly showTabs?: boolean;
}

const dashboardOptions = [
  {
    value: 'financial' as DashboardType,
    label: 'Financial Dashboard',
    description: 'Asset values, depreciation, ROI & compliance',
    icon: BarChart3,
    testId: 'dashboard-tab-financial',
  },
  {
    value: 'asset-management' as DashboardType,
    label: 'Maintenance Dashboard',
    description: 'Maintenance, health, activity & operations',
    icon: Package,
    testId: 'dashboard-tab-asset-management',
  },
];

export function DashboardTabs({
  value,
  onChange,
  className,
  showTabs = true,
}: Readonly<DashboardTabsProps>) {
  const { useSavePreferences } = useDashboard();
  const savePreferences = useSavePreferences();

  const handleChange = async (newValue: DashboardType) => {
    onChange(newValue);
    
    // Save preference to localStorage immediately
    try {
      localStorage.setItem('selectedDashboard', newValue);
    } catch (error) {
      console.error('Failed to save dashboard preference to localStorage:', error);
    }
    
    // Save preference to backend
    try {
      await savePreferences.mutateAsync({
        selectedDashboardType: newValue,
      });
    } catch (error) {
      console.error('Failed to save dashboard preference to backend:', error);
    }
  };

  // If only one dashboard is available, don't show tabs
  if (!showTabs) {
    return null;
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {dashboardOptions.map((option) => {
        const Icon = option.icon;
        const isActive = option.value === value;
        
        return (
          <button
            key={option.value}
            onClick={() => handleChange(option.value)}
            data-testid={option.testId}
            className={cn(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all rounded-md',
              'hover:bg-muted/50',
              isActive
                ? 'text-foreground bg-muted shadow-sm'
                : 'text-muted-foreground'
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="whitespace-nowrap">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
